import { useState, useRef, useEffect } from 'react'
import { Tooltip } from 'antd'
import {
  SearchOutlined,
  SortAscendingOutlined,
  TableOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import { Button } from '../Button'
import type { TableSearchProps, TableSearchIconButtonsProps } from './TableSearch.types'

/**
 * TableSearch Component
 *
 * A reusable search component for tables with expandable search popup.
 * Features collapsed state with search icon and expanded popup with
 * search input, buttons, and optional filter link.
 *
 * @example
 * // Basic usage
 * <TableSearch
 *   value={searchTerm}
 *   onChange={setSearchTerm}
 *   onSearch={handleSearch}
 *   onReset={handleReset}
 *   onFilter={handleFilter}
 * />
 *
 * @example
 * // Without filter
 * <TableSearch
 *   value={searchTerm}
 *   onChange={setSearchTerm}
 *   onSearch={handleSearch}
 *   onReset={handleReset}
 *   showFilter={false}
 * />
 */
export function TableSearch({
  value,
  onChange,
  onSearch,
  onReset,
  onFilter,
  placeholder = 'Search name & keywords',
  showFilter = true,
  filterLabel = 'Filter',
  loading = false,
  className = '',
}: TableSearchProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Handle click outside to collapse
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Only collapse if search value is empty
        if (!value) {
          setIsExpanded(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [value])

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isExpanded])

  // Keep expanded if there's a value
  useEffect(() => {
    if (value) {
      setIsExpanded(true)
    }
  }, [value])

  const handleExpand = () => {
    setIsExpanded(true)
  }

  const handleSearch = () => {
    onSearch()
    setIsExpanded(false)
  }

  const handleReset = () => {
    onChange('')
    onReset()
    setIsExpanded(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
    if (e.key === 'Escape') {
      if (!value) {
        setIsExpanded(false)
      }
    }
  }

  return (
    <div ref={containerRef} className={`table-search-wrapper ${className}`}>
      {/* Collapsed search bar - always visible */}
      <div className="table-search-container">
        <div className="table-search-input-wrapper">
          <input
            type="text"
            placeholder={placeholder}
            className="table-search-input-collapsed"
            onFocus={handleExpand}
            onClick={handleExpand}
            value={value}
            readOnly
          />
        </div>
        <button
          type="button"
          className="table-search-icon-button"
          onClick={handleExpand}
          aria-label="Search"
        >
          <SearchOutlined />
        </button>
      </div>

      {/* Expanded popup panel */}
      {isExpanded && (
        <div className="table-search-popup">
          <input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className="table-search-input-expanded"
          />
          <div className="table-search-actions">
            <button
              type="button"
              className="table-search-btn-primary"
              onClick={handleSearch}
              disabled={loading}
            >
              <SearchOutlined />
              <span>Search</span>
            </button>
            <button
              type="button"
              className="table-search-btn-secondary"
              onClick={handleReset}
            >
              Reset
            </button>
            {showFilter && onFilter && (
              <button
                type="button"
                className="table-search-btn-link"
                onClick={onFilter}
              >
                {filterLabel}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * TableSearchIconButtons Component
 *
 * Icon buttons commonly used alongside TableSearch for table operations.
 *
 * @example
 * <TableSearchIconButtons
 *   onSort={handleSort}
 *   onManageColumns={handleManageColumns}
 *   onDelete={handleDelete}
 * />
 */
export function TableSearchIconButtons({
  onSort,
  onManageColumns,
  onDelete,
  showSort = true,
  showManageColumns = true,
  showDelete = true,
  sortTooltip = 'Sort',
  manageColumnsTooltip = 'Manage Columns',
  deleteTooltip = 'Delete',
}: TableSearchIconButtonsProps) {
  return (
    <div className="table-search-icon-buttons">
      {showSort && onSort && (
        <Tooltip title={sortTooltip}>
          <button
            type="button"
            className="table-search-icon-btn"
            onClick={onSort}
            aria-label={sortTooltip}
          >
            <SortAscendingOutlined />
          </button>
        </Tooltip>
      )}
      {showManageColumns && onManageColumns && (
        <Tooltip title={manageColumnsTooltip}>
          <button
            type="button"
            className="table-search-icon-btn"
            onClick={onManageColumns}
            aria-label={manageColumnsTooltip}
          >
            <TableOutlined />
          </button>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip title={deleteTooltip}>
          <button
            type="button"
            className="table-search-icon-btn"
            onClick={onDelete}
            aria-label={deleteTooltip}
          >
            <DeleteOutlined />
          </button>
        </Tooltip>
      )}
    </div>
  )
}

// Export types
export type { TableSearchProps, TableSearchIconButtonsProps }
